// Stock — data access.
//
//   overview  rpc stock_overview(warehouse?, term?, after_code, limit 51)
//             items with on-hand <> 0, keyset on item code; per item one probe
//             of inventory_balances_key (tenant_id, item_id …). 1.3 ms per page,
//             4.7 ms with search, at 100k items / 2M ledger rows.
//   detail    rpc item_stock(item) — balances by warehouse/location/lot plus the
//             latest 50 movements in one round-trip (≈ 7 ms, ≈ 20 KB).
//   Balances and the ledger are written only by the posting engine; the UI
//   never writes them.

import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { Page } from "@/components/DataList";
import { isDemoMode, supabase } from "@/lib/supabase";
import { demoApi } from "./demo";
import type { ListFilter } from "@patronix/domain/list";

export const transactionTypes = [
  "adjustment_in",
  "adjustment_out",
  "transfer_out",
  "transfer_in",
  "purchase_receipt",
  "purchase_return",
  "sales_issue",
  "sales_return",
  "production_issue",
  "production_receipt",
  "count_adjustment",
] as const;
export type TransactionType = (typeof transactionTypes)[number];

export interface StockRow {
  item_id: string;
  code: string;
  description: string;
  item_type: "material" | "semi_finished" | "finished_good" | "service";
  lot_controlled: boolean;
  base_uom_code: string;
  base_uom_decimals: number;
  on_hand: number | string;
  /** Reserved for open sales order lines. */
  reserved: number | string;
  /** On hand − reserved − on hand in blocked locations / lots (may be negative). */
  available: number | string;
  locations: number;
  /** Part of on hand that is project stock (PR 3a); never available to free demand. */
  project_on_hand?: number | string;
}

export interface StockBalance {
  warehouse_id: string;
  warehouse_code: string;
  company_code: string;
  location_id: string;
  location_code: string;
  lot_id: string | null;
  lot_code: string | null;
  expiry_date: string | null;
  lot_blocked: boolean | null;
  /** Project stock (PR 3a); null = free stock. */
  project_id?: string | null;
  project_code?: string | null;
  on_hand: number | string;
}

export interface StockMovement {
  id: number;
  posted_at: string;
  transaction_type: TransactionType;
  quantity: number | string;
  entered_qty: number | string;
  entered_uom_code: string;
  warehouse_code: string;
  location_code: string;
  lot_code: string | null;
  source_document_type: string;
  source_document_id: string;
  reason_code: string | null;
  reverses_id: number | null;
  project_code?: string | null;
}

export interface ItemStock {
  /** First 200 non-zero balances; balances_has_more tells there are more. */
  balances: StockBalance[];
  balances_has_more: boolean;
  total_on_hand: number | string;
  movements: StockMovement[];
}

export interface StockListParams {
  warehouseId: string; // "" = all warehouses
  q: string;
  /** Column filters on the item (stockListColumns); the list is always by item code. */
  filters?: ListFilter[];
}

export const STOCK_PAGE = 50;

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}

export async function listStock(p: StockListParams, cursor: string | null, signal: AbortSignal, size = STOCK_PAGE): Promise<Page<StockRow>> {
  if (isDemoMode) return demoApi.listStock(p, cursor);
  const { data, error } = await db()
    .rpc("stock_overview", {
      p_warehouse_id: p.warehouseId || null,
      p_term: p.q.trim() || null,
      p_after_code: cursor,
      p_limit: size + 1,
      p_filters: p.filters ?? [],
    })
    .abortSignal(signal);
  if (error) throw error;
  const rows = data as StockRow[];
  const hasMore = rows.length > size;
  const page = hasMore ? rows.slice(0, size) : rows;
  return { rows: page, nextCursor: hasMore ? page[page.length - 1]!.code : null };
}

async function getItemStock(itemId: string, signal: AbortSignal): Promise<ItemStock> {
  if (isDemoMode) return demoApi.getItemStock(itemId);
  const { data, error } = await db().rpc("item_stock", { p_item_id: itemId }).abortSignal(signal);
  if (error) throw error;
  return data as ItemStock;
}

export const stockKeys = {
  all: ["warehousing", "stock"] as const,
  list: (p: StockListParams) => [...stockKeys.all, "list", p] as const,
  item: (id: string) => [...stockKeys.all, "item", id] as const,
};

// Stock changes with every posting: short staleTime, background refetch.
const STALE = 15_000;

export function stockListQuery(p: StockListParams) {
  return infiniteQueryOptions({
    queryKey: stockKeys.list(p),
    queryFn: ({ pageParam, signal }) => listStock(p, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Page<StockRow>) => last.nextCursor,
    staleTime: STALE,
  });
}

export function itemStockQuery(itemId: string) {
  return queryOptions({
    queryKey: stockKeys.item(itemId),
    queryFn: ({ signal }) => getItemStock(itemId, signal),
    staleTime: STALE,
  });
}
